import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useGraphql } from 'utils/hooks/useGraphql';

import noop from 'utils/noop';
import { InputText } from 'ui/inputs';
import { BUTTON_SIZES, GhostLinkButton } from 'ui/button';
import { RadioList } from 'ui/radioList';
import { HelpToggletip, TipPlacement } from 'ui/tooltip';
import { colors } from 'ui/theme';
import { TimeOfDay } from 'modules/workflows/types';
import { DateWrapper, InputWrapper, Subtitle2 } from '../DelayEditor.styles';
import { SCHEDULE_TYPES } from '../DelayEditor.constants';

export const TOD_TIME_INPUT_ID = 'tod-time-input-field';
export const TOD_DATE_INPUT_ID = 'tod-date-input-field';

type ScheduleType = TimeOfDay['scheduleType'];

export interface TimeOfDayFormProps extends Partial<TimeOfDay> {
  /** The time that is selected */
  time?: string;
  /** The date that is selected */
  date?: string;
  /** The schedule type */
  scheduleType?: ScheduleType;
  /** Is the form readonly? */
  isReadOnly?: boolean;
  /** Callback for when the time is updated */
  onTimeUpdate?: (e: string) => void;
  /** Callback for when the date is updated */
  onDateUpdate?: (e?: string) => void;
  /** Callback for when the schedule type is updated */
  onScheduleTypeUpdate?: (e: ScheduleType) => void;
}

export const getProjectTimezone = gql`
  query getProjectTimezone {
    currentProject {
      timeZone
    }
  }
`;

export const TimeOfDayForm = ({
  time = '',
  date = '',
  scheduleType,
  isReadOnly = false,
  onTimeUpdate = noop,
  onDateUpdate = noop,
  onScheduleTypeUpdate = noop,
}: TimeOfDayFormProps) => {
  const { data: projectData } = useGraphql(getProjectTimezone, {
    fetchPolicy: 'cache-first',
  });

  const basedOnOptions = useMemo(
    () => [
      {
        id: SCHEDULE_TYPES.PROJECT_TIME_ZONE,
        name: `Project time zone ${
          projectData?.currentProject?.timeZone ? `(${projectData?.currentProject?.timeZone})` : ''
        }`,
        value: SCHEDULE_TYPES.PROJECT_TIME_ZONE,
        isDisabled: isReadOnly,
      },
      {
        id: SCHEDULE_TYPES.RECIPIENT_TIME_ZONE,
        name: `Recipient’s time zone (ip or timeZone field required)`,
        value: SCHEDULE_TYPES.RECIPIENT_TIME_ZONE,
        isDisabled: isReadOnly,
      },
    ],
    [isReadOnly, projectData?.currentProject?.timeZone],
  );

  return (
    <fieldset>
      <InputWrapper>
        <InputText
          isReadOnly={isReadOnly}
          type="time"
          autoComplete="on"
          name="time"
          id="time"
          aria-label="time"
          value={time}
          data-test={TOD_TIME_INPUT_ID}
          onChange={onTimeUpdate}
        />
      </InputWrapper>

      <DateWrapper>
        <InputText
          isReadOnly={isReadOnly}
          id="date"
          type="date"
          autoComplete="on"
          value={date}
          aria-label="date"
          data-test={TOD_DATE_INPUT_ID}
          onChange={onDateUpdate}
        />
        {!isReadOnly && (
          <GhostLinkButton onClick={() => onDateUpdate()} size={BUTTON_SIZES.SMALL}>
            Clear
          </GhostLinkButton>
        )}
        <HelpToggletip
          placement={TipPlacement.Right}
          tooltipContent="If no date is set, this will default to the soonest date possible"
          iconProps={{
            color: colors.secondaryText,
          }}
        />
      </DateWrapper>

      <Subtitle2>Based on...</Subtitle2>

      <RadioList
        name="timezone-based-on"
        options={basedOnOptions}
        value={scheduleType}
        onSelect={onScheduleTypeUpdate}
      />
    </fieldset>
  );
};
